import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };
const abs = (u) => !u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`;

// Best-effort embed detection
const embedFromUrl = (raw) => {
  if (!raw) return null;
  const url = raw.trim();
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <Layout>
        <section className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">/ 404</div>
          <h1 className="heading text-4xl font-black uppercase tracking-tight mt-2">This story isn&apos;t published.</h1>
          <Link to="/blog" className="btn-primary mt-8 inline-block">← Back to the journal</Link>
        </section>
      </Layout>
    );
  }
  if (!post) return <Layout><div className="max-w-[900px] mx-auto px-6 py-24 mono text-xs uppercase tracking-[0.2em]">Loading…</div></Layout>;

  const embed = embedFromUrl(post.video_url);
  const isUploadedVideo = post.video_url && (post.video_url.startsWith("/api/blog-file/") || post.video_url.endsWith(".mp4"));

  return (
    <Layout>
      <article className="max-w-[900px] mx-auto px-6 py-14" data-testid="blog-post">
        <Link to="/blog" className="mono text-[10px] uppercase tracking-[0.25em] text-[#555] hover:text-[#C77E3C]">← Back to the journal</Link>
        <div className="mt-3 mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">{fmt(post.published_at || post.created_at)} · by {post.author_name || "Jubasmash"}</div>
        <h1 data-testid="blog-post-title" className="heading text-4xl sm:text-6xl font-black uppercase tracking-tighter mt-2">{post.title}</h1>

        {post.cover_image_url && (
          <div className="mt-8 brut-border overflow-hidden">
            <img src={abs(post.cover_image_url)} alt={post.title} className="w-full max-h-[560px] object-cover" />
          </div>
        )}

        {post.video_url && (
          <div className="mt-8 brut-border bg-black overflow-hidden aspect-video">
            {embed ? (
              <iframe title={post.title} src={embed} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            ) : isUploadedVideo ? (
              <video controls className="w-full h-full bg-black" src={abs(post.video_url)} />
            ) : (
              <a href={post.video_url} target="_blank" rel="noreferrer" className="grid place-items-center text-white p-8">Open video ↗</a>
            )}
          </div>
        )}

        {post.body && (
          <div className="prose-brut mt-10 text-[17px] leading-[1.7] text-[#222] whitespace-pre-wrap" data-testid="blog-post-body">
            {post.body}
          </div>
        )}

        {post.images && post.images.length > 0 && (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="blog-gallery">
            {post.images.map((u, i) => (
              <img key={i} src={abs(u)} alt="" className="brut-border w-full object-cover max-h-[420px]" />
            ))}
          </div>
        )}
      </article>
    </Layout>
  );
}
